/**
 * orderChangeWording: how the Order Changes surface names a garment and
 * describes what changed (BLU-1257).
 *
 * ONE formatter for the card, the resolver plan, the plan summary and the
 * prefilled reason, so the same order line can never be described two ways.
 *
 * Pure on purpose: no React, no i18n import. Functions that need copy take a
 * `t`. Translation keys are spelled out as literals in `KEYS` (the way
 * `LAST_SEEN_KEYS` does in `CancelledItemCard.tsx`) so `npm run check:i18n`
 * can see every one of them.
 *
 * THE NAME IS LOOKED UP, NEVER DECODED. The garment name comes from the order
 * line's own stored title, colour and size (`order_items`, attached server-side
 * by `order-line-display.ts`). Nothing here reads meaning out of SKU letters.
 * The only string operation on stored data is the exact-suffix rule in
 * `garmentLabel`. When no name is stored the SKU stands in; nothing is ever
 * blank, and no action is ever held back because a name is missing.
 */

/** The stored display fields of one order line. Mirrors the backend
 *  `OrderLineDisplay`. */
export interface GarmentDisplay {
  productName: string | null;
  variantTitle: string | null;
  color: string | null;
  size: string | null;
}

/** The slice of a coverage line (or a stored issue line) the wording needs. */
export interface WordingLine {
  sku?: string | null;
  /** What the customer wants now. */
  expected: number;
  /** Live garments in production for this line. */
  active: number;
  /** Shortfall: garments still to make. */
  under: number;
  /** Surplus: garments to cancel. */
  over: number;
  /** BLU-869: true only when Shopify now ships this surplus from another location. */
  shipsFromAnotherLocation?: boolean;
  /** BLU-869: beside the flag only. What the customer wants now, uncapped. */
  customerQuantity?: number;
  /** Optional: a server one deploy behind sends none. */
  display?: GarmentDisplay | null;
}

/** A translator bound to the `production` namespace. */
export type Translate = (key: string, params?: Record<string, string | number>) => string;

export type LineChangeKind = 'removed' | 'reduced' | 'added' | 'increased' | 'elsewhere';

const KEYS = {
  unknownSku: 'issues.orderChanges.planUnknownSku',
  elsewherePartial: 'issues.orderChanges.change.elsewherePartial',
  prefillElsewherePartial: 'issues.orderChanges.prefillElsewherePartial',
  planLineDetail: 'issues.orderChanges.planLineDetail',
  planLineDetailSplitHalf: 'issues.orderChanges.planLineDetailSplitHalf',
  planCancelNamed: 'issues.orderChanges.planCancelNamed',
  planMakeNamed: 'issues.orderChanges.planMakeNamed',
  planSummaryNamedBoth: 'issues.orderChanges.planSummaryNamedBoth',
  planSummaryNamedCancel: 'issues.orderChanges.planSummaryNamedCancel',
  planSummaryNamedMake: 'issues.orderChanges.planSummaryNamedMake',
} as const;

/** One sentence of the prefilled reason per kind of change. */
const PREFILL_KEYS: Record<LineChangeKind, string> = {
  removed: 'issues.orderChanges.prefillRemoved',
  reduced: 'issues.orderChanges.prefillReduced',
  added: 'issues.orderChanges.prefillAdded',
  increased: 'issues.orderChanges.prefillIncreased',
  elsewhere: 'issues.orderChanges.prefillElsewhere',
};

/** One line of the Order Changes card per kind of change. */
const CHANGE_KEYS: Record<LineChangeKind, string> = {
  removed: 'issues.orderChanges.change.removed',
  reduced: 'issues.orderChanges.change.reduced',
  added: 'issues.orderChanges.change.added',
  increased: 'issues.orderChanges.change.increased',
  elsewhere: 'issues.orderChanges.change.elsewhere',
};

/** Both reasons (`cancelSurplusUnits.reason`, `acceptOrderChanges.notes`) are
 *  capped at this many characters by the server's input schemas. */
export const REASON_MAX_LENGTH = 500;

/** Shopify's placeholder variant title for a product with no options. */
const DEFAULT_VARIANT_TITLE = 'Default Title';

function clean(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * "Baby Tee, Black, S" from the order line's stored fields, or null when no
 * usable name is stored.
 *
 * `order_items.product_name` is Shopify's line name: the product title followed
 * by " - " and the variant title. Printing it beside the colour and size would
 * say both twice, so the suffix is removed, but ONLY when the name ends with
 * exactly " - " plus the stored variant title. Nothing else is split or
 * decoded: "Basics Romper - Sage - 0-3M" with variant "0-3M" keeps "- Sage".
 *
 * After the name: colour and size when BOTH are stored; else the whole variant
 * title; else whichever of colour or size is stored; else nothing.
 */
export function garmentLabel(display: GarmentDisplay | null | undefined): string | null {
  if (!display) return null;
  let name = clean(display.productName);
  if (!name) return null;

  const storedVariant = clean(display.variantTitle);
  const variant = storedVariant === DEFAULT_VARIANT_TITLE ? '' : storedVariant;
  if (variant) {
    const suffix = ` - ${variant}`;
    if (name.length > suffix.length && name.endsWith(suffix)) {
      name = name.slice(0, -suffix.length).trim();
    }
  }

  const color = clean(display.color);
  const size = clean(display.size);
  const parts = [name];
  if (color && size) parts.push(color, size);
  else if (variant) parts.push(variant);
  else if (color) parts.push(color);
  else if (size) parts.push(size);
  return parts.join(', ');
}

/**
 * What happened to one line, in the server's own terms. Mirrors
 * `classifyChangeKinds` (backend `order-change-issues.ts`) for a single line:
 * a surplus on a line the customer no longer wants is a removal, otherwise a
 * quantity cut; a shortfall with nothing in production is a new line, otherwise
 * a quantity increase. A surplus Shopify now ships from another location
 * (BLU-869, split orders) is named as that, before removed / reduced.
 * `count` is the surplus or the shortfall; for a moved line it is how many of
 * the customer's garments moved (`customerQuantity - expected`), capped at the
 * surplus, so plain surplus made here is never called moved.
 */
export function lineChange(
  line: Pick<WordingLine, 'expected' | 'active' | 'under' | 'over' | 'shipsFromAnotherLocation' | 'customerQuantity'>,
): { kind: LineChangeKind; count: number } | null {
  if (line.over > 0 && line.shipsFromAnotherLocation === true) {
    return { kind: 'elsewhere', count: movedCount(line) ?? line.over };
  }
  if (line.over > 0) {
    return { kind: line.expected === 0 ? 'removed' : 'reduced', count: line.over };
  }
  if (line.under > 0) {
    return { kind: line.active === 0 ? 'added' : 'increased', count: line.under };
  }
  return null;
}

/**
 * How many of the customer's garments Shopify moved off this location:
 * `customerQuantity - expected`, capped at the surplus. Null when the line
 * does not carry the customer quantity (or it is not above `expected`).
 */
function movedCount(line: Pick<WordingLine, 'expected' | 'over' | 'customerQuantity'>): number | null {
  const wanted = line.customerQuantity;
  if (typeof wanted !== 'number' || wanted <= line.expected) return null;
  return Math.min(line.over, wanted - line.expected);
}

/**
 * BLU-869: the params of a PARTIAL move (part of a line moved while this
 * location still ships some of it), or null when the line is not one. The one
 * gate the card sentence and the prefilled reason share, so the two can never
 * disagree on whether a move is whole or partial.
 */
function partialMoveParams(
  line: WordingLine,
  change: { kind: LineChangeKind; count: number },
  t: Translate,
): Record<string, string | number> | null {
  if (change.kind !== 'elsewhere' || line.expected <= 0 || movedCount(line) === null) return null;
  return { garment: garmentText(line, t), moved: change.count, from: line.customerQuantity as number };
}

/** "Baby Tee, Black, S" for one; "2 × Baby Tee, Black, S" above one. */
export function withQuantity(count: number, label: string): string {
  return count > 1 ? `${count} × ${label}` : label;
}

/**
 * The text that stands in for the garment: its name when one is stored, else
 * its SKU, else the "Unknown SKU" copy. Never empty.
 */
export function garmentText(line: Pick<WordingLine, 'sku' | 'display'>, t: Translate): string {
  return garmentLabel(line.display) ?? (clean(line.sku) || t(KEYS.unknownSku));
}

/**
 * The first line of a plan row when the garment has a stored name:
 * "Cancel Baby Tee, Black, S" / "Make 2 × Baby Tee, Black, L". Returns null
 * when no name is stored, so the caller keeps the count wording and the SKU.
 */
export function planRowVerb(tone: 'cancel' | 'make', line: WordingLine, t: Translate): string | null {
  const label = garmentLabel(line.display);
  if (!label) return null;
  const count = tone === 'cancel' ? line.over : line.under;
  return t(tone === 'cancel' ? KEYS.planCancelNamed : KEYS.planMakeNamed, {
    garment: withQuantity(count, label),
  });
}

/**
 * The translation parameters for one changed line. A removal, an addition or a
 * garment that now ships from another location states its quantity as a prefix ("2 × ..."); a quantity change does not,
 * because the sentence already says from how many to how many (`active` is
 * what is in production, `expected` is what the customer wants now).
 */
function changeParams(
  line: WordingLine,
  change: { kind: LineChangeKind; count: number },
  t: Translate,
): Record<string, string | number> {
  const text = garmentText(line, t);
  if (change.kind === 'removed' || change.kind === 'added' || change.kind === 'elsewhere') {
    return { garment: withQuantity(change.count, text) };
  }
  return { garment: text, from: line.active, to: line.expected };
}

/** Changed lines only, surplus first and shortfall after: the plan-row order. */
export function inPlanOrder<L extends WordingLine>(lines: L[]): L[] {
  return [...lines.filter((l) => l.over > 0), ...lines.filter((l) => l.over <= 0 && l.under > 0)];
}

/**
 * One line of the Order Changes card: "Baby Tee, Black, S removed",
 * "2 × Baby Tee, Black, L added", "Baby Tee, Black, S: quantity reduced from 3
 * to 1". The SKU stands in when no name is stored. Null for a healthy line.
 */
export function changeSentence(line: WordingLine, t: Translate): string | null {
  const change = lineChange(line);
  if (!change) return null;
  // BLU-869: part of a line moved while this location still ships some of it.
  // Say how many of how many, like a quantity change, rather than "now ships
  // from another location", which would read as the whole line.
  const partial = partialMoveParams(line, change, t);
  if (partial) return t(KEYS.elsewherePartial, partial);
  return t(CHANGE_KEYS[change.kind], changeParams(line, change, t));
}

/**
 * The second line of a plan row: what this order owes against what is in
 * production. A split half (BLU-869, judged against its own Shopify
 * fulfilment order) owes what THIS location ships, which can be less than the
 * customer wants, so it says that instead of "Customer wants".
 */
export function planLineDetail(
  line: Pick<WordingLine, 'expected' | 'active'>,
  isSplitHalf: boolean,
  t: Translate,
): string {
  return t(isSplitHalf ? KEYS.planLineDetailSplitHalf : KEYS.planLineDetail, {
    expected: line.expected,
    active: line.active,
  });
}

/**
 * The prefilled reason: one full sentence per changed line, one per row of the
 * box, removals and cuts first.
 *
 *   Customer removed Baby Tee, Black, S from the order.
 *   Customer added Baby Tee, Black, L to the order.
 *
 * A removal and an addition are never merged into one "swapped" sentence:
 * nothing stored links the two lines, so pairing them would be a guess.
 *
 * Never longer than `maxLength`: sentences are added whole, and the text stops
 * before the first one that would not fit, so the box can never hold more than
 * the server accepts and a fix is never refused for the length of a prefill.
 */
export function buildReasonText(
  lines: WordingLine[],
  t: Translate,
  maxLength: number = REASON_MAX_LENGTH,
): string {
  let text = '';
  for (const line of inPlanOrder(lines)) {
    const change = lineChange(line);
    if (!change) continue;
    // A partial move says how many of how many here too: this sentence is also
    // the cancel reason on the garment and the Accept-as-is note.
    const partial = partialMoveParams(line, change, t);
    const sentence = partial
      ? t(KEYS.prefillElsewherePartial, partial)
      : t(PREFILL_KEYS[change.kind], changeParams(line, change, t));
    const next = text === '' ? sentence : `${text}\n${sentence}`;
    if (next.length > maxLength) break;
    text = next;
  }
  return text;
}

/**
 * The one-line plan summary by name ("Cancel Baby Tee, Black, S. Make Baby
 * Tee, Black, L."), or null when the count summary is the honest one: more
 * than one line on either side, a line with no stored name, or a cancel total
 * that is not the cancel line's own surplus (the caller caps how many units
 * one call cancels).
 */
export function planSummaryText(
  cancelLines: WordingLine[],
  makeLines: WordingLine[],
  totalOver: number,
  t: Translate,
): string | null {
  if (cancelLines.length > 1 || makeLines.length > 1) return null;
  const cancelLine = cancelLines[0];
  const makeLine = makeLines[0];
  if (!cancelLine && !makeLine) return null;
  if (totalOver !== (cancelLine?.over ?? 0)) return null;

  const cancelLabel = cancelLine ? garmentLabel(cancelLine.display) : null;
  const makeLabel = makeLine ? garmentLabel(makeLine.display) : null;
  if (cancelLine && !cancelLabel) return null;
  if (makeLine && !makeLabel) return null;

  const cancelText = cancelLine && cancelLabel ? withQuantity(cancelLine.over, cancelLabel) : null;
  const makeText = makeLine && makeLabel ? withQuantity(makeLine.under, makeLabel) : null;
  if (cancelText && makeText) {
    return t(KEYS.planSummaryNamedBoth, { cancel: cancelText, make: makeText });
  }
  if (cancelText) return t(KEYS.planSummaryNamedCancel, { garment: cancelText });
  if (makeText) return t(KEYS.planSummaryNamedMake, { garment: makeText });
  return null;
}

/** The keys of the resolver's "nothing to apply" state. Literals for check:i18n. */
const STATE_KEYS = {
  okTitle: 'issues.orderChanges.stateOkTitle',
  okDesc: 'issues.orderChanges.stateOkDesc',
  neverTitle: 'issues.orderChanges.stateNeverTitle',
  neverDesc: 'issues.orderChanges.stateNeverDesc',
  splitTitle: 'issues.orderChanges.stateSplitTitle',
  splitDesc: 'issues.orderChanges.stateSplitDesc',
  splitStaleDesc: 'issues.orderChanges.stateSplitStaleDesc',
  ambiguousTitle: 'issues.orderChanges.stateAmbiguousTitle',
  ambiguousDesc: 'issues.orderChanges.stateAmbiguousDesc',
} as const;

export interface NonActionableCopy {
  titleKey: string;
  descriptionKey: string;
  tone: 'ok' | 'neutral';
}

/**
 * What the resolver says when a verdict leaves nothing to apply, in the
 * operator's language. A split half whose Shopify fulfilment orders have not
 * caught up with the order (`splitDetail` 'fo_stale', BLU-869) says so: it
 * clears on the next sync, so sending the operator to the Split Orders tab
 * would be wrong. A failed read arrives as `eval_error`, so it gets the
 * "refresh in a moment" state, never the split one.
 */
export function nonActionableCopy(coverage: {
  verdict: string;
  ambiguityReason?: string | null;
  splitDetail?: string | null;
}): NonActionableCopy {
  if (coverage.verdict === 'ok') {
    return { titleKey: STATE_KEYS.okTitle, descriptionKey: STATE_KEYS.okDesc, tone: 'ok' };
  }
  if (coverage.verdict === 'never_fanned_out') {
    return { titleKey: STATE_KEYS.neverTitle, descriptionKey: STATE_KEYS.neverDesc, tone: 'neutral' };
  }
  if (coverage.ambiguityReason === 'split_order') {
    return {
      titleKey: STATE_KEYS.splitTitle,
      descriptionKey: coverage.splitDetail === 'fo_stale' ? STATE_KEYS.splitStaleDesc : STATE_KEYS.splitDesc,
      tone: 'neutral',
    };
  }
  return { titleKey: STATE_KEYS.ambiguousTitle, descriptionKey: STATE_KEYS.ambiguousDesc, tone: 'neutral' };
}
